import React, { useState, useEffect } from 'react';
import { Modal } from '../common/Modal';
import { PilgrimSelector } from './modal/PilgrimSelector';
import { useCampAccommodationStore } from '../../stores/campAccommodationStore';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { User, MapPin, Package, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { canManageBed } from '../../utils/campAccommodationPermissions';
import { useLanguage } from '../../contexts/LanguageContext';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  tableId: string;
  tentNumber: number;
  hallNumber: number;
  bedIndex: number;
  gender: 'male' | 'female';
};

export const CampBedModal = ({
  isOpen,
  onClose,
  tableId,
  tentNumber,
  hallNumber,
  bedIndex,
  gender,
}: Props) => {
  const { translations } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [canManage, setCanManage] = useState(false);
  const { assignPilgrim, removePilgrim, tables } = useCampAccommodationStore();
  const userRole = localStorage.getItem('userRole');
  const userId = localStorage.getItem('userId');

  const table = tables[tableId];
  const tent = table?.tents.find((t) => t.tentNumber === tentNumber);
  const hall = tent?.halls.find((h) => h.hallNumber === hallNumber);
  const bed = hall?.beds[bedIndex];

  // Check permissions when bed data changes
  useEffect(() => {
    const checkPermissions = async () => {
      if (!userId || !userRole) return;
      const hasPermission = await canManageBed(bed, userRole, userId);
      setCanManage(hasPermission);
    };
    checkPermissions();
  }, [bed, userId, userRole]);

  const handleAssign = async (pilgrim: any) => {
    if (!canManage) {
      toast.error(
        translations?.campBedModal?.noPermissionAssign ||
          'You do not have permission to assign pilgrims'
      );
      return;
    }

    try {
      setIsLoading(true);
      await assignPilgrim(
        tableId,
        tentNumber,
        hallNumber,
        bedIndex,
        pilgrim.Passport_Number
      );
      toast.success(
        translations?.campBedModal?.assignSuccess ||
          'Pilgrim assigned successfully'
      );
      onClose();
    } catch (error: any) {
      console.error('Assignment error:', error);
      toast.error(
        error.message ||
          translations?.campBedModal?.assignError ||
          'Failed to assign pilgrim'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemove = async () => {
    if (!canManage) {
      toast.error(
        translations?.campBedModal?.noPermissionRemove ||
          'You do not have permission to remove pilgrims'
      );
      return;
    }

    try {
      setIsLoading(true);
      await removePilgrim(tableId, tentNumber, hallNumber, bedIndex);
      toast.success(
        translations?.campBedModal?.removeSuccess ||
          'Pilgrim removed successfully'
      );
      onClose();
    } catch (error: any) {
      console.error('Remove error:', error);
      toast.error(
        error.message ||
          translations?.campBedModal?.removeError ||
          'Failed to remove pilgrim'
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${translations?.campBedModal?.tent || 'Tent'} ${tentNumber} - ${
        translations?.campBedModal?.hall || 'Hall'
      } ${hallNumber} - ${translations?.campBedModal?.bed || 'Bed'} ${
        bedIndex + 1
      }`}
    >
      <div className="space-y-6">
        {/* Current Bed Info */}
        {bed?.pilgrimId ? (
          <div className="bg-gray-50 p-4 rounded-lg space-y-4">
            <div className="flex items-center gap-3">
              <div
                className={`p-2 rounded-full ${
                  gender === 'male' ? 'bg-blue-50' : 'bg-pink-50'
                }`}
              >
                <User
                  className={
                    gender === 'male' ? 'text-blue-500' : 'text-pink-500'
                  }
                  size={20}
                />
              </div>
              <div>
                <div className="font-medium">{bed.pilgrimName}</div>
                <div className="text-sm text-gray-500 flex items-center gap-2">
                  <MapPin size={14} />
                  {bed.country}
                </div>
                <div className="text-sm text-gray-500 flex items-center gap-2">
                  <Package size={14} />
                  {bed.packageName}
                </div>
              </div>
            </div>

            {canManage && (
              <button
                onClick={handleRemove}
                className="w-full px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                {translations?.campBedModal?.removeButton || 'Remove Pilgrim'}
              </button>
            )}

            {!canManage && (
              <div className="flex items-center gap-2 text-yellow-600 bg-yellow-50 p-3 rounded-lg">
                <AlertCircle size={18} />
                <span className="text-sm">
                  {translations?.campBedModal?.noPermissionManage ||
                    'You do not have permission to manage this bed'}
                </span>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {canManage ? (
              <>
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <p className="text-yellow-800">
                    {translations?.campBedModal?.assignInstructions ||
                      'Please select a pilgrim to assign to this bed. Ensure the pilgrim:'}
                  </p>
                  <ul className="mt-2 text-sm text-yellow-700 list-disc list-inside">
                    <li>
                      {translations?.campBedModal?.assignRule1 ||
                        'Has not been assigned elsewhere'}
                    </li>
                    <li>
                      {(translations?.campBedModal?.assignRule2 || 'Matches the hall type ({gender})').replace(
                        '{gender}',
                        gender === 'male'
                          ? translations?.campBedModal?.male || 'Male'
                          : translations?.campBedModal?.female || 'Female'
                      )}
                    </li>
                    <li>
                      {translations?.campBedModal?.assignRule3 ||
                        'Is associated with an approved guide'}
                    </li>
                  </ul>
                </div>

                <PilgrimSelector onSelect={handleAssign} gender={gender} />
              </>
            ) : (
              <div className="flex items-center gap-2 text-yellow-600 bg-yellow-50 p-4 rounded-lg">
                <AlertCircle size={18} />
                <span>
                  {translations?.campBedModal?.noPermissionAssign ||
                    'You do not have permission to assign pilgrims'}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};